import type { QueryClient } from "@tanstack/react-query";

// The over-target reads' cache keys and "Where to save"'s stale-mark, apart
// from the reads themselves (`spec-over-target.ts`) so the write paths — the
// one updater every Editor verb, clone and publish lands through
// (`applyEditorResult`) — reach them without loading the reads' server
// functions, as the history keys do (`spec-history-keys.ts`).

export const overTargetSelectionsQueryKey = (specificationId: number) =>
  ["specifications", "over-target", specificationId] as const;

export const whereToSaveQueryKey = (specificationId: number) =>
  ["specifications", "where-to-save", specificationId] as const;

/**
 * A spec write's effect on "Where to save": its rows marked stale, so the
 * dialog's rows follow every Editor write — a swap that leaves the count
 * where it was included (#1243 D2) — and not only a count move. An observed
 * entry reads again in the background, its rows kept on screen meanwhile; an
 * unobserved one reads again when the band next mounts.
 *
 * **A read still out is cancelled first.** It was sent before the write, so
 * it must never land as the rows: TanStack's refetch cancels a refetch over
 * rows (`cancelRefetch`) but joins one onto a first read in flight.
 */
export function markWhereToSaveStale(
  queryClient: QueryClient,
  specificationId: number,
): Promise<void> {
  const filters = { queryKey: whereToSaveQueryKey(specificationId), exact: true };
  // Settles once marked, never on the re-read it starts.
  return queryClient.cancelQueries(filters).then(() => {
    void queryClient.invalidateQueries(filters);
  });
}
